import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { applyFootwearSizeRegionToSection } from '@theme/variant-picker';
import { fetchConfig, preloadImage } from '@theme/utilities';
import { bindCardVariationHover } from '@theme/card-variation-hover';

/**
 * Gets the section IDs to fetch for soft navigation.
 * Dynamically detects sections on the current page.
 * @returns {string[]} Array of section IDs.
 */
const getProductSections = () => {
  /** @type {string[]} */
  const sections = [];

  const mainSection = document.querySelector('[id^="shopify-section-"][id*="__main"], #shopify-section-main');
  if (mainSection) {
    const sectionId = mainSection.id.replace('shopify-section-', '');
    sections.push(sectionId);
  } else {
    sections.push('main');
  }

  const recommendationsSection = document.querySelector('[id^="shopify-section-"][id*="recommendations"]');
  if (recommendationsSection) {
    const sectionId = recommendationsSection.id.replace('shopify-section-', '');
    sections.push(sectionId);
  }

  return sections;
};

/**
 * Manages prefetching and caching of product sections using the Section Rendering API.
 * Implements stale-while-revalidate: serves cached data immediately and refreshes in background.
 * Singleton instance shared across all ProductVariations components.
 */
class VariationPrefetchManager {
  /** @type {Map<string, { promise: Promise<Record<string, string>>, timestamp: number }>} */
  #cache = new Map();

  /** @type {number} */
  #maxCacheSize = 20;

  /** @type {number} Cache TTL in milliseconds (60 seconds). */
  #ttl = 60_000;

  /**
   * Prefetches product sections using Section Rendering API.
   * Returns cached data immediately if fresh; triggers background revalidation if stale.
   * @param {string} url - The product URL to prefetch.
   * @returns {Promise<Record<string, string>> | undefined} Promise resolving to section HTML map.
   */
  prefetch(url) {
    if (!url) return undefined;

    const cached = this.#cache.get(url);
    const now = Date.now();

    if (cached) {
      if (now - cached.timestamp < this.#ttl) return cached.promise;
      // Stale: update timestamp immediately to prevent concurrent revalidations
      this.#cache.set(url, { ...cached, timestamp: now });
      this.#fetchAndCache(url); // background revalidation, no abort signal
      return cached.promise;
    }

    return this.#fetchAndCache(url);
  }

  /**
   * Gets cached sections or fetches them. Accepts an AbortSignal for cancellation.
   * @param {string} url - The product URL.
   * @param {AbortSignal} [signal] - Optional signal to cancel the fetch.
   * @returns {Promise<Record<string, string>>} Promise resolving to section HTML map.
   */
  async get(url, signal) {
    const cached = this.#cache.get(url);
    if (cached) return cached.promise;

    return this.#fetchAndCache(url, signal);
  }

  /**
   * Fetches and stores sections in cache.
   * @param {string} url - The product URL.
   * @param {AbortSignal} [signal] - Optional abort signal.
   * @returns {Promise<Record<string, string>>} Promise resolving to section HTML map.
   */
  #fetchAndCache(url, signal) {
    const sectionUrl = this.#buildSectionUrl(url);

    const fetchPromise = fetch(sectionUrl, {
      credentials: 'same-origin',
      ...(signal ? { signal } : {}),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .catch((error) => {
        this.#cache.delete(url); // remove failed/aborted entries so they can be retried
        throw error;
      });

    this.#cache.set(url, { promise: fetchPromise, timestamp: Date.now() });
    this.#trimCache();

    return fetchPromise;
  }

  /**
   * Builds Section Rendering API URL. Forwards any `variant` query param
   * through so the server renders the target page with that variant
   * selected.
   *
   * @param {string} url - The product URL.
   * @returns {string} The section rendering URL.
   */
  #buildSectionUrl(url) {
    const [baseUrl, query] = url.split('?');
    const sections = getProductSections().join(',');
    const params = new URLSearchParams(query || '');
    const variantId = params.get('variant');
    return variantId
      ? `${baseUrl}?sections=${sections}&variant=${encodeURIComponent(variantId)}`
      : `${baseUrl}?sections=${sections}`;
  }

  /**
   * Checks if a URL has been prefetched.
   * @param {string} url - The URL to check.
   * @returns {boolean} True if prefetched.
   */
  has(url) {
    return this.#cache.has(url);
  }

  /**
   * Trims the cache to prevent memory issues.
   */
  #trimCache() {
    if (this.#cache.size <= this.#maxCacheSize) return;

    const keysToDelete = Array.from(this.#cache.keys()).slice(0, this.#cache.size - this.#maxCacheSize);
    for (const key of keysToDelete) {
      this.#cache.delete(key);
    }
  }
}

const prefetchManager = new VariationPrefetchManager();

/**
 * Product variations component - handles thumbnails with navigation or checkbox selection
 *
 * @extends {Component}
 */
export class ProductVariations extends Component {
  /** @type {string} */
  #selectionMode = 'navigate';

  /** @type {HTMLInputElement[]} */
  #checkboxes = [];

  /** @type {boolean} */
  #isLoading = false;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #loadingTimer = null;

  /** @type {AbortController | null} */
  #currentFetchController = null;

  /** @type {HTMLImageElement | null} The gallery image currently showing a hover preview. */
  #previewedImage = null;

  /** @type {{ src: string, srcset: string } | null} That image's own source, captured before the preview so it can be restored. */
  #previewedOriginal = null;

  /** True once a click has begun soft-navigating, so a trailing mouseleave doesn't restore the preview mid-navigation. */
  #navigatingAway = false;

  /** @type {(() => void) | null} Cleanup for the card-context hover/select binding (see connectedCallback). */
  #unbindCardHover = null;

  connectedCallback() {
    super.connectedCallback();

    this.swatchesContainer = this.querySelector('.product-card__swatches');
    this.#selectionMode = this.dataset.selectionMode || 'navigate';

    if (!this.swatchesContainer) {
      return;
    }

    if (this.#selectionMode === 'checkbox') {
      this.#initializeCheckboxMode();
      return;
    }

    // Soft-nav below only applies standalone on the PDP; inside a product-card, use card_hover's binding instead.
    const productCard = this.closest('product-card') || this.closest('.resource-card');
    if (productCard) {
      this.#unbindCardHover = bindCardVariationHover(this, productCard);
      return;
    }

    this.#initializeNavigateMode();
    this.#initializePopstateHandler();
  }

  /** @type {((event: PopStateEvent) => void) | null} */
  #popstateHandler = null;

  /**
   * Initialize popstate handler for browser back/forward navigation.
   */
  #initializePopstateHandler() {
    this.#popstateHandler = this.#handlePopstate.bind(this);
    if (this.#popstateHandler) {
      window.addEventListener('popstate', this.#popstateHandler);
    }
  }

  /**
   * Handle browser back/forward navigation.
   * Updates DOM only — does NOT push a new history state.
   * @param {PopStateEvent} event - The popstate event.
   */
  async #handlePopstate(event) {
    if (event.state?.softNav || document.location.pathname.includes('/products/')) {
      if (this.#currentFetchController) this.#currentFetchController.abort();
      this.#currentFetchController = new AbortController();
      try {
        await this.#updatePageContent(document.location.href, this.#currentFetchController.signal);
      } finally {
        this.#currentFetchController = null;
      }
    }
  }

  /**
   * Initialize navigate mode: prefetches section HTML and preloads images on hover,
   * then intercepts clicks for soft navigation.
   */
  #initializeNavigateMode() {
    if (!this.swatchesContainer) return;

    const thumbnails = this.swatchesContainer.querySelectorAll('.product-card__thumbnail');
    if (thumbnails.length === 0) return;

    thumbnails.forEach((thumbnail) => {
      if (!(thumbnail instanceof HTMLElement)) return;

      // Warm the sibling product once (section HTML + its main image) so a
      // click soft-navigates instantly. mouseenter = desktop hover;
      // pointerdown = touch (fires before click).
      let warmed = false;
      const warm = () => {
        if (warmed) return;
        warmed = true;
        const url = thumbnail.dataset.variationUrl;
        if (url) prefetchManager.prefetch(url)?.catch(() => {});
        const image = thumbnail.dataset.variationImage;
        if (image) {
          const srcset = thumbnail.dataset.variationImageSrcset || undefined;
          // Prefer the live gallery image's `sizes` so the preload picks the
          // exact srcset candidate the main image will actually render.
          const galleryImg = this.closest('[id^="shopify-section"]')?.querySelector(
            'media-gallery img.product-media__image',
          );
          const rawSizes = galleryImg?.sizes || thumbnail.dataset.variationImageSizes;
          const sizes = rawSizes?.replace(/^\s*auto\s*,\s*/i, '') || undefined;
          preloadImage(image, { srcset, sizes, fetchpriority: true });
        }
      };

      // Desktop: preview this colourway in the main gallery image while hovering.
      thumbnail.addEventListener('mouseenter', () => {
        warm();
        this.#previewVariationImage(thumbnail);
      });
      thumbnail.addEventListener('mouseleave', () => this.#restoreVariationImage());

      // Touch: no hover — just warm the sibling on first touch.
      thumbnail.addEventListener('pointerdown', warm);

      thumbnail.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Keep the previewed image visible through the soft-nav; the section
        // morph then replaces it with the real gallery for the new colourway.
        this.#navigatingAway = true;
        this.#handleVariationClick(thumbnail);
      });
    });

    const navigateButtons = this.querySelectorAll('[data-variation-navigate-button]');
    navigateButtons.forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const url = button.dataset.variationUrl;
        if (url && !button.hasAttribute('disabled')) this.#softNavigate(url);
      });
    });
  }

  /**
   * Resolve the image in the MAIN gallery area right now — the visible slide,
   * not always the first. The slideshow marks off-screen slides
   * `aria-hidden="true"` (active = `aria-hidden="false"`); for non-slideshow
   * (grid) layouts we fall back to the first media image. Re-queried on every
   * hover so the preview tracks whichever slide the shopper is looking at.
   * @returns {HTMLImageElement | null}
   */
  #getActiveGalleryImage() {
    const gallery = this.closest('[id^="shopify-section"]')?.querySelector('media-gallery');
    if (!gallery) return null;
    const img =
      gallery.querySelector('slideshow-slide[aria-hidden="false"] img.product-media__image') ||
      gallery.querySelector('slideshow-slide:not([aria-hidden="true"]) img.product-media__image') ||
      gallery.querySelector('img.product-media__image');
    return img instanceof HTMLImageElement ? img : null;
  }

  /**
   * Preview a colourway by pointing the current main gallery image at the
   * variation's image on hover. The browser keeps the current frame painted
   * until the new source decodes, so there's no blank flash. The image's own
   * source is captured once so #restoreVariationImage can put it back.
   * @param {HTMLElement} thumbnail
   */
  #previewVariationImage(thumbnail) {
    // The current colour is already shown in the gallery — no peek on its own swatch.
    if (thumbnail.classList.contains('is-active')) return;
    const src = thumbnail.dataset.variationImage;
    if (!src) return;
    const img = this.#getActiveGalleryImage();
    if (!img) return;

    // Active slide changed since the last preview — restore the old one first.
    if (this.#previewedImage && this.#previewedImage !== img) this.#restoreVariationImage();

    if (this.#previewedImage !== img) {
      this.#previewedImage = img;
      this.#previewedOriginal = { src: img.src, srcset: img.srcset };
    }
    img.src = src;
    img.srcset = thumbnail.dataset.variationImageSrcset || '';
  }

  /** Restore the previewed gallery image to the product's own source. */
  #restoreVariationImage() {
    // After a click we're soft-navigating to the sibling product; keep the
    // preview so the gallery doesn't flash back before the section morphs in.
    if (this.#navigatingAway) return;
    const img = this.#previewedImage;
    if (!img || !this.#previewedOriginal) return;
    img.src = this.#previewedOriginal.src;
    img.srcset = this.#previewedOriginal.srcset;
    this.#previewedImage = null;
    this.#previewedOriginal = null;
  }

  /**
   * Initialize checkbox mode
   */
  #initializeCheckboxMode() {
    // Support both old checkbox style and new button style
    this.#checkboxes = Array.from(this.querySelectorAll('.product-card__variation-checkbox'));
    const buttons = Array.from(this.querySelectorAll('[data-variation-button]'));

    if (this.#checkboxes.length === 0 && buttons.length === 0) {
      return;
    }

    // Handle old checkbox style
    this.#checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => this.#handleCheckboxChange());
    });

    // Handle new button style
    buttons.forEach((button) => {
      button.addEventListener('click', (e) => {
        if (e instanceof MouseEvent) {
          this.#handleButtonClick(e);
        }
      });
    });

    this.#updateSummary();
  }

  /**
   * Handle checkbox change
   */
  #handleCheckboxChange() {
    this.#updateSummary();
  }

  /**
   * Handle button click for add/remove variations
   * @param {MouseEvent} event - The click event
   */
  #handleButtonClick(event) {
    const button = event.currentTarget;
    if (!(button instanceof HTMLElement)) return;

    const card = button.closest('.product-variation-card');
    if (!card) return;

    // Toggle selection
    const isSelected = card.classList.contains('is-selected');

    if (isSelected) {
      card.classList.remove('is-selected');
      button.querySelector('.product-variation-card__button-text')?.replaceWith(
        Object.assign(document.createElement('span'), {
          className: 'product-variation-card__button-text',
          textContent: this.dataset.i18nAdd || 'Add',
        }),
      );
    } else {
      card.classList.add('is-selected');
      button.querySelector('.product-variation-card__button-text')?.replaceWith(
        Object.assign(document.createElement('span'), {
          className: 'product-variation-card__button-text',
          textContent: '✓ Added',
        }),
      );
    }

    this.#updateSummary();
  }

  /**
   * Update legend with selected product names and external trigger
   */
  #updateSummary() {
    // Get selected items from either checkboxes or cards
    const selectedCheckboxes = this.#checkboxes.filter((cb) => cb.checked);
    const selectedCards = Array.from(this.querySelectorAll('.product-variation-card.is-selected')).filter(
      (el) => el instanceof HTMLElement,
    );

    /** @type {Array<HTMLInputElement|HTMLElement>} */
    const selectedItems = selectedCheckboxes.length > 0 ? selectedCheckboxes : selectedCards;
    const count = selectedItems.length;

    // Update selected items in legend
    const selectedItemsElement = this.querySelector('.product-variations__selected-items');
    if (selectedItemsElement) {
      if (count === 0) {
        selectedItemsElement.textContent = '';
      } else {
        // Get all selected product names
        let names;
        if (selectedCheckboxes.length > 0) {
          names = selectedCheckboxes
            .map((cb) => {
              const wrapper = cb.closest('[data-variation-title]');
              return wrapper instanceof HTMLElement ? wrapper.dataset.variationTitle : null;
            })
            .filter(Boolean);
        } else {
          names = selectedCards
            .map((card) => {
              if (!(card instanceof HTMLElement)) return null;
              return card.dataset.variationTitle || null;
            })
            .filter(Boolean);
        }
        selectedItemsElement.textContent = names.length > 0 ? names.join(', ') : '';
      }
    }

    // Update external trigger button
    this.#updateTriggerButton(selectedItems);
  }

  /**
   * Update the trigger button outside the drawer with selected products
   * @param {Array<HTMLInputElement|HTMLElement>} selectedItems - Array of selected items (checkboxes or cards)
   */
  #updateTriggerButton(selectedItems) {
    // Find the drawer component and trigger button
    const drawer = this.closest('product-variations-drawer');
    if (!drawer) return;

    const triggerSelected = drawer.querySelector('[data-trigger-selected]');
    const triggerImage = drawer.querySelector('[data-trigger-image]');
    const triggerImageWrapper = drawer.querySelector('[data-trigger-image-wrapper]');

    if (!triggerSelected) return;

    if (selectedItems.length === 0) {
      triggerSelected.textContent = '';

      // Hide image when nothing is selected
      if (triggerImageWrapper instanceof HTMLElement) {
        triggerImageWrapper.style.display = 'none';
      }
      return;
    }

    // Get selected product names and images
    const products = selectedItems
      .map((item) => {
        let wrapper;
        if (item instanceof HTMLInputElement) {
          // Old checkbox style
          wrapper = item.closest('[data-variation-title]');
        } else if (item instanceof HTMLElement) {
          // New card style
          wrapper = item;
        }
        if (!(wrapper instanceof HTMLElement)) return null;
        return {
          title: wrapper.dataset.variationTitle || null,
          image: wrapper.dataset.variationImage || null,
        };
      })
      .filter((p) => p !== null && !!p.title);

    if (products.length === 0) return;

    const names = products.map((p) => p && p.title).filter(Boolean);

    // Check if all selected products have the same name
    const allSameName = names.every((name) => name === names[0]);

    // Format the text
    const firstName = names[0];
    if (!firstName) return;

    if (allSameName) {
      // All same product: "2 x cushion"
      triggerSelected.textContent = `${names.length} x ${firstName}`;
    } else {
      // Different products: show first name + count if multiple
      if (names.length === 1) {
        triggerSelected.textContent = firstName;
      } else {
        triggerSelected.textContent = `${firstName} +${names.length - 1}`;
      }
    }

    // Update image - show first product's image
    const firstProduct = products[0];
    if (
      triggerImage instanceof HTMLImageElement &&
      triggerImageWrapper instanceof HTMLElement &&
      firstProduct &&
      firstProduct.image
    ) {
      const imageUrl = firstProduct.image;
      // Generate appropriate image size URL (120x120 for display)
      const sizedImageUrl = imageUrl.includes('cdn.shopify.com')
        ? imageUrl.replace(/\.(jpg|jpeg|png|webp|gif)/i, '_120x120.$1')
        : imageUrl;

      triggerImage.src = sizedImageUrl;
      triggerImage.alt = firstName || '';
      triggerImageWrapper.style.display = 'block';
    } else if (triggerImageWrapper instanceof HTMLElement) {
      triggerImageWrapper.style.display = 'none';
    }
  }

  /**
   * Get selected variation IDs (public method for integration with add to cart)
   * @returns {string[]} Array of variant IDs
   */
  getSelectedVariations() {
    if (this.#selectionMode !== 'checkbox') {
      return [];
    }

    // Check for old checkbox style
    const selectedCheckboxes = this.#checkboxes.filter((cb) => cb.checked && !cb.disabled);
    if (selectedCheckboxes.length > 0) {
      return selectedCheckboxes.map((cb) => cb.value);
    }

    // Check for new card style
    const selectedCards = Array.from(this.querySelectorAll('.product-variation-card.is-selected:not(.is-unavailable)'));
    return selectedCards
      .map((card) => {
        if (!(card instanceof HTMLElement)) return null;
        return card.dataset.variationId || null;
      })
      .filter((id) => id !== null);
  }

  /**
   * Check if checkbox mode is active and has selections
   * @returns {boolean}
   */
  hasSelectedVariations() {
    return this.#selectionMode === 'checkbox' && this.getSelectedVariations().length > 0;
  }

  /**
   * Clear all selections
   */
  clearSelections() {
    // Clear old checkbox style
    this.#checkboxes.forEach((cb) => (cb.checked = false));

    // Clear new card style
    const selectedCards = this.querySelectorAll('.product-variation-card.is-selected');
    selectedCards.forEach((card) => {
      card.classList.remove('is-selected');
      const button = card.querySelector('[data-variation-button]');
      if (button) {
        const buttonText = button.querySelector('.product-variation-card__button-text');
        if (buttonText) {
          buttonText.textContent = this.dataset.i18nAdd || 'Add';
        }
      }
    });

    this.#updateSummary();
  }

  /**
   * Add selected variations to cart (called by product form)
   * @param {string|null} currentVariantId - The current product variant ID
   * @returns {Promise<Object>} Result object with success status and data
   */
  async addSelectedToCart(currentVariantId = null) {
    const selectedVariantIds = this.getSelectedVariations();
    /** @type {string[]} */
    const allVariantIds = [];

    if (currentVariantId) {
      allVariantIds.push(currentVariantId);
    }

    selectedVariantIds.forEach((id) => {
      if (!allVariantIds.includes(id)) {
        allVariantIds.push(id);
      }
    });

    if (allVariantIds.length === 0) {
      return {
        success: false,
        error: this.dataset.i18nNoProducts || 'No products to add',
      };
    }

    try {
      const items = allVariantIds.map((id) => ({
        id: parseInt(id, 10),
        quantity: 1,
      }));

      const config = fetchConfig('javascript');
      const headers = {
        ...config.headers,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ items }),
      });

      const responseText = await response.text();

      if (!response.ok) {
        let errorMessage = this.dataset.i18nCartError || 'Error adding items to cart';
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.message || errorData.description || errorMessage;
        } catch {
          errorMessage =
            response.status === 422 ? this.dataset.i18nUnavailable || 'Some products are unavailable' : errorMessage;
        }

        return {
          success: false,
          error: errorMessage,
        };
      }

      const data = JSON.parse(responseText);

      if (data.items) {
        // @ts-ignore - data.items is from JSON response
        const addedItemsCount = data.items.reduce((sum, item) => sum + (item.quantity || 0), 0);

        this.dispatchEvent(
          new CartAddEvent({}, data.items[0].variant_id.toString(), {
            source: 'product-form-component',
            itemCount: addedItemsCount,
            productId: this.dataset.productId,
            sections: data.sections,
          }),
        );

        setTimeout(() => {
          this.clearSelections();
        }, 500);

        return {
          success: true,
          data: data,
          itemCount: data.items.length,
        };
      } else {
        return {
          success: false,
          error: this.dataset.i18nUnexpected || 'Unexpected server response',
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || this.dataset.i18nCartError || 'Error adding items to cart',
      };
    }
  }

  /**
   * Handle variation thumbnail click
   * @param {HTMLElement} thumbnail - The clicked thumbnail
   */
  async #handleVariationClick(thumbnail) {
    if (thumbnail.classList.contains('is-active') || this.#isLoading) {
      return;
    }

    const variationUrl = thumbnail.dataset.variationUrl;

    if (!variationUrl) {
      return;
    }

    if (this.swatchesContainer) {
      this.swatchesContainer
        .querySelectorAll('.product-card__thumbnail')
        .forEach((t) => t.classList.remove('is-active'));
    }
    thumbnail.classList.add('is-active');

    if (!thumbnail.hasAttribute('onclick')) {
      await this.#softNavigate(variationUrl);
    }
  }

  /**
   * Navigates to a variation URL: updates DOM and pushes a new history state.
   * Cancels any in-flight navigation request before starting a new one.
   * @param {string} url - The product URL to navigate to.
   */
  async #softNavigate(url) {
    if (this.#currentFetchController) this.#currentFetchController.abort();
    this.#currentFetchController = new AbortController();

    try {
      await this.#updatePageContent(url, this.#currentFetchController.signal);
      history.pushState({ softNav: true }, '', url);
    } finally {
      this.#currentFetchController = null;
    }
  }

  /**
   * Core DOM update: fetches sections and applies them to the page.
   * Used by both #softNavigate (click) and #handlePopstate (back/forward).
   * Does NOT modify history — callers are responsible for that.
   * @param {string} url - The product URL to fetch.
   * @param {AbortSignal} [signal] - Optional signal to cancel the fetch.
   */
  async #updatePageContent(url, signal) {
    this.#isLoading = true;
    this.#setLoadingState(true);

    try {
      const sections = await prefetchManager.get(url, signal);

      if (!sections || typeof sections !== 'object') {
        throw new Error('Invalid sections response');
      }

      // Parse all section HTML upfront — docs are reused for both DOM update and title extraction
      const parsedSections = Object.entries(sections)
        .filter(([, html]) => html && typeof html === 'string')
        .map(([sectionId, html]) => ({
          sectionId,
          doc: new DOMParser().parseFromString(/** @type {string} */ (html), 'text/html'),
        }));

      const doUpdate = () => {
        for (const { sectionId, doc } of parsedSections) {
          this.#updateSectionFromDoc(sectionId, doc);
        }

        // Extract title from first section's already-parsed doc (no second DOMParser call)
        const firstDoc = parsedSections[0]?.doc;
        if (firstDoc) {
          const h1 = firstDoc.querySelector('h1');
          if (h1) document.title = `${h1.textContent?.trim() ?? ''} – ${window.Shopify?.shop ?? ''}`;
        }
      };

      // Use View Transitions API if available for smooth animated swap
      if (typeof document.startViewTransition === 'function') {
        await document.startViewTransition(doUpdate).finished;
      } else {
        doUpdate();
      }
    } catch (error) {
      if (/** @type {Error} */ (error).name === 'AbortError') return;
      window.location.href = url;
    } finally {
      this.#isLoading = false;
      this.#setLoadingState(false);
    }
  }

  /**
   * Updates a section in the DOM using an already-parsed Document.
   * Reuses the doc object provided by #updatePageContent to avoid double parsing.
   * @param {string} sectionId - The section ID from the API response.
   * @param {Document} doc - The pre-parsed document from DOMParser.
   */
  #updateSectionFromDoc(sectionId, doc) {
    let currentSection = document.querySelector(`#shopify-section-${sectionId}`);
    if (!currentSection) {
      currentSection = document.querySelector(`[id^="shopify-section-"][id$="__${sectionId}"]`);
    }
    if (!currentSection) return;

    const newSectionWrapper = doc.querySelector('.shopify-section') || doc.body.firstElementChild;
    if (!newSectionWrapper) return;

    // Strip prefetch/preload links injected by liquid snippets (e.g. product-variations.liquid).
    // They're correct on initial page load but cause a batch re-fetch of all variation images
    // on every swap — with the now-correct imagesizes, each link selects the full gallery-width
    // source. The JS hover preload already handles targeted preloading during navigation.
    for (const link of newSectionWrapper.querySelectorAll('link[rel="prefetch"], link[rel="preload"]')) {
      link.remove();
    }

    // Swatch thumbnails default to loading="lazy"; on a section rebuild that
    // defers them and they blink in on a colour switch (visible on mobile).
    // They're cached — identical across colourways — so eager-load and
    // synchronously decode them here, before the swap, so they paint in the
    // same frame instead of flashing blank. Scoped to the small swatch images
    // only — never the full-size gallery media, which must stay lazy.
    for (const img of newSectionWrapper.querySelectorAll('.product-card__thumbnail img')) {
      img.setAttribute('loading', 'eager');
      img.setAttribute('decoding', 'sync');
    }

    const currentId = currentSection.id;
    const currentClasses = currentSection.className;

    currentSection.innerHTML = newSectionWrapper.innerHTML;
    currentSection.id = currentId;
    currentSection.className = currentClasses;

    this.#reinitializeExternalScripts(currentSection);

    currentSection.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
    applyFootwearSizeRegionToSection(currentSection);
  }

  /**
   * Re-executes external scripts inside a section after an innerHTML swap.
   * The browser does not re-run scripts inserted via innerHTML, so we replace them.
   * Custom Elements are NOT manually reinitialized here — the browser calls
   * connectedCallback automatically when new elements are inserted into the DOM.
   * @param {Element} container - The container element.
   */
  #reinitializeExternalScripts(container) {
    const scripts = container.querySelectorAll('script[src]');
    for (const oldScript of scripts) {
      if (!(oldScript instanceof HTMLScriptElement)) continue;
      const newScript = document.createElement('script');
      newScript.type = oldScript.type || 'module';
      newScript.src = oldScript.src;
      oldScript.replaceWith(newScript);
    }
  }

  /**
   * Sets loading state on the component.
   * @param {boolean} loading - Whether loading is in progress.
   */
  #setLoadingState(loading) {
    if (this.#loadingTimer) {
      clearTimeout(this.#loadingTimer);
      this.#loadingTimer = null;
    }

    if (loading) {
      this.#loadingTimer = setTimeout(() => {
        this.#loadingTimer = null;
        this.classList.add('is-loading');
        const productInfo = document.querySelector('#shopify-section-main');
        if (productInfo) {
          productInfo.classList.add('is-loading');
          productInfo.setAttribute('aria-busy', 'true');
        }
      }, 400);
    } else {
      this.classList.remove('is-loading');
      const productInfo = document.querySelector('#shopify-section-main');
      if (productInfo) {
        productInfo.classList.remove('is-loading');
        productInfo.setAttribute('aria-busy', 'false');
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#loadingTimer) {
      clearTimeout(this.#loadingTimer);
      this.#loadingTimer = null;
    }

    if (this.#popstateHandler) {
      window.removeEventListener('popstate', this.#popstateHandler);
      this.#popstateHandler = null;
    }

    if (this.#currentFetchController) {
      this.#currentFetchController.abort();
      this.#currentFetchController = null;
    }

    this.#unbindCardHover?.();
    this.#unbindCardHover = null;
  }
}

if (!customElements.get('product-variations')) {
  customElements.define('product-variations', ProductVariations);
}
